import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';
import { Link } from 'react-router-dom';

import BackIcon from '@/assets/icons/back.svg?react';
import CloseIcon from '@/assets/icons/close-button.svg?react';
import { ApplicationModalTitle } from '@/components/molecules/ApplicationModalTitle';
import { useTranslator } from '@/hooks/useTranslator';

interface Props {
  readonly title?: string;
  readonly description?: string | React.ReactElement;
  readonly variant?: 'normal' | 'prominent';

  readonly back?: string;
  readonly width?: number;

  readonly backDisabled?: boolean;

  onClose?(): void;
}

export const ApplicationModal: React.FC<React.PropsWithChildren<Props>> = (
  props: React.PropsWithChildren<Props>,
): React.ReactElement => {
  const { onClose } = props;
  const {
    title,
    description,
    children,
    back,
    width = 744,
    variant = 'normal',
    backDisabled = false,
  } = props;
  const { location } = window;

  const tr = useTranslator();

  const CloseButton = React.useCallback(
    function CloseButton(): React.ReactElement {
      if (!onClose) {
        return <></>;
      }

      return (
        <IconButton
          sx={styles.closeButton}
          aria-label="Close"
          color="inherit"
          disabled={backDisabled}
          onClick={onClose}>
          <CloseIcon fill="currentColor" />
        </IconButton>
      );
    },
    [backDisabled, onClose],
  );

  const BackButton = React.useCallback(
    function BackButton(): React.ReactElement {
      if (back) {
        const url = ['..', location.search].join('/');

        return (
          <IconButton
            aria-label={tr('BACK')}
            role="button"
            color="inherit"
            component={Link}
            disabled={backDisabled}
            to={url}>
            <BackIcon fill="currentColor" />
          </IconButton>
        );
      } else {
        return <></>;
      }
    },
    [back, backDisabled, location.search, tr],
  );

  const containerStyle = React.useMemo(
    (): readonly SystemStyleObject[] => [
      styles.container,
      {
        width: width,
      },
    ],
    [width],
  );

  return (
    <Paper sx={containerStyle} elevation={1}>
      <Box sx={styles.titleBar}>
        <Box sx={styles.titleButtons}>
          <BackButton />
          <CloseButton />
        </Box>
        {title && (
          <ApplicationModalTitle variant={variant} title={title} description={description} />
        )}
      </Box>

      <Box sx={styles.content}>{children}</Box>
    </Paper>
  );
};

const styles: Record<string, SystemStyleObject> = {
  container: {
    position: 'relative',
    maxHeight: '100%',
    scrollbarGutter: 'stable both-edges',
    scrollbarWidth: 'thin',
    overflowY: 'auto',
  },
  content: {
    mx: 7,
    mb: 8,
  },
  closeButton: {
    ml: 'auto',
  },
  titleButtons: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: 88,
    color: 'text.primary',
    '&:empty': {
      height: 0,
      mt: 8,
    },
  },
  titleBar: {
    position: 'sticky',
    px: 3,
    pb: 0,
    top: 0,
    width: '100%',
    color: 'text.primary',
    backgroundColor: 'custom.OffWhite',
    zIndex: 2,
  },
  cancelButton: {
    mt: 3,
    display: 'flex',
    justifyContent: 'center',
  },
};
